import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { GridWrapper } from './styles';
import { INcsScoresReport } from '@nkr/models/index';
import { ColDef, ColSpanParams, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { OverlayTrigger } from 'react-bootstrap';
import PopoverTemplate from '@nkr/components/popover-template';

interface IProps {
    data: INcsScoresReport[],
    totals: INcsScoresReport[],
    onGridReady: (e:GridReadyEvent)=>void
}

const DetailGrid = (props: IProps) => {
    const onGridReady = (e: GridReadyEvent) => {
        props.onGridReady(e);
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        // get the current grids width
        let gridWidth = document.getElementById('grid-wrapper').offsetWidth;

        // keep track of which columns to hide/show
        let columnsToShow = [];
        let columnsToHide = [];

        // iterate over all columns (visible or not) and work out
        // now many columns can fit (based on their minWidth)
        let totalColsWidth = 0;
        let allColumns = e.columnApi.getAllColumns();

        for (let i = 0; i < allColumns.length; i++) {
            let column = allColumns[i];
            totalColsWidth += column.getMinWidth();

            if (totalColsWidth > gridWidth) {
                columnsToHide.push(column.getColId);
            } else {
                columnsToShow.push(column.getColId);
            }
        }

        // show/hide columns based on current grid width
        e.columnApi.setColumnsVisible(columnsToShow, true);
        e.columnApi.setColumnsVisible(columnsToHide, false);

        // fill out any available space to ensure there are no gaps
        e.api.sizeColumnsToFit();
    };

    const columns: ColDef[] = [
        {
            colId: 'Rank',
            field: 'rank',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    R#
                </SortableHeaderComponent>,
            headerName: 'Rank',
            width: 55,
            sortable: false,
            colSpan: (params: ColSpanParams) => {
                let data = params.data as INcsScoresReport;

                if (data.centerId != 0) {
                    return 1;
                } else {
                    return 3;
                }
            },
            valueGetter: (params: ValueGetterParams) => {
                if (params.data.ctrId != 0) {
                    return params.node.rowIndex + 1
                } else {
                    return 'TOTALS';
                }
            }
        },
        {
            colId: 'centerAlias',
            field: 'centerAlias',
            headerName: 'Center',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
            width: 150,
        },
        {
            colId: 'ctrIsAdpPartner',
            field: 'ctrIsAdpPartner',
            headerName: 'Partner',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    Partner
                </SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as INcsScoresReport;

                if (data.centerId != 0) {
                    return params.value ? 'Yes' : 'No';
                } else {
                    return params.value;
                }
            },
            width: 100,
        },
        {
            colId: 'nddStarts',
            field: 'nddStarts',
            headerName: 'NDD Starts',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    NDD<br />Starts
                </SortableHeaderComponent>,
            width: 80,
        },
        {
            colId: 'adpStarts',
            field: 'adpStarts',
            headerName: 'V Starts',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    Voucher<br />Starts
                </SortableHeaderComponent>,
            width: 90,
        },
        {
            colId: 'totalStarts',
            field: 'totalStarts',
            headerName: 'Total Starts',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    Total<br />Starts
                </SortableHeaderComponent>,
            width: 80,
        },
        {
            colId: 'nddEnds',
            field: 'nddEnds',
            headerName: 'WLPs',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    WLPs
                </SortableHeaderComponent>,
            width: 80,
        },
        {
            colId: 'adpEnds',
            field: 'adpEnds',
            headerName: 'VH Ends',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    Voucher<br />Holder<br />Ends
                </SortableHeaderComponent>,
            width: 85,
        },
        {
            colId: 'totalEnds',
            field: 'totalEnds',
            headerName: 'Total Ends',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    Total<br />Ends
                </SortableHeaderComponent>,
            width: 70,
        },

        {
            colId: 'nddncSa',
            field: 'nddncSa',
            headerName: 'NDD NCSa',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    NDD<br />NCSa
                </SortableHeaderComponent>,
            width: 70,
        },

        {
            colId: 'adpncSa',
            field: 'adpncSa',
            headerName: 'V NCSa',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    Voucher<br />NCSa
                </SortableHeaderComponent>,
            width: 90,
        },

        {
            colId: 'detailsNCSa',
            field: 'detailsNCSa',
            headerName: 'Total NCSa',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    Total<br />NCSa
                </SortableHeaderComponent>,
            width: 90,
        },

        {
            colId: 'fcstNDDStarts',
            field: 'fcstNDDStarts',
            headerName: 'Forecast NDD Starts',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    Forecast<br />NDD<br />Starts
                </SortableHeaderComponent>,
            width: 115,
        },

        {
            colId: 'fcstADPStarts',
            field: 'fcstADPStarts',
            headerName: 'Forecast V Starts',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    Forecast<br />Voucher<br />Starts
                </SortableHeaderComponent>,
            width: 115,
        },

        {
            colId: 'fcstTotalStarts',
            field: 'fcstTotalStarts',
            headerName: 'Forecast Total Starts',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    Forecast<br />Total<br />Starts
                </SortableHeaderComponent>,
            width: 115,
        },

        {
            colId: 'fcstNDDEnds',
            field: 'fcstNDDEnds',
            headerName: 'Forecast NDD Ends',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    Forecast<br />NDD<br />Ends
                </SortableHeaderComponent>,
            width: 115,
        },

        {
            colId: 'fcstADPEnds',
            field: 'fcstADPEnds',
            headerName: 'Forecast VH Ends',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    Forecast<br />Voucher Holder<br />Ends
                </SortableHeaderComponent>,
            width: 115,
        },

        {
            colId: 'fcstTotalEnds',
            field: 'fcstTotalEnds',
            headerName: 'Forecast Total Ends',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    Forecast<br />Total<br />Ends
                </SortableHeaderComponent>,
            width: 115,
        },

        {
            colId: 'fcstNDDNCS',
            field: 'fcstNDDNCS',
            headerName: 'Forecast NDD NCSb',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    Forecast<br />NDD<br />NCSb
                </SortableHeaderComponent>,
            width: 115,
        },

        {
            colId: 'fcstADPNCS',
            field: 'fcstADPNCS',
            headerName: 'Forecast V NCSb',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    Forecast<br />Voucher<br />NCSb
                </SortableHeaderComponent>,
            width: 115,
        },

        {
            colId: 'fcstNCS',
            field: 'fcstNCS',
            headerName: 'Forecast Total NCSb',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    Forecast<br />Total<br />NCSb
                </SortableHeaderComponent>,
            width: 115,
        },

        {
            colId: 'declineRatio',
            field: 'declineRatio',
            headerName: 'WL Decline Ratio',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    WL<br />Decline<br />Ratio
                </SortableHeaderComponent>,
            width: 100,
            cellRendererFramework: (params: ICellRendererParams) => {
                return (
                    <OverlayTrigger trigger={['hover', 'hover']} overlay={<PopoverTemplate dismiss={false}>
                        <table>
                            <tbody>
                                <tr>
                                    <td style={{ paddingRight: 20 }}>WL Declines L6:</td>
                                    <td style={{ textAlign: 'right' }}>{params.data.wlpDeclines}</td>
                                </tr>
                                <tr>
                                    <td style={{ paddingRight: 20 }}>WL Offers L6:</td>
                                    <td style={{ textAlign: 'right' }}>{params.data.wlpOffers}</td>
                                </tr>
                            </tbody>
                        </table>
                    </PopoverTemplate>}>
                        <div>{params.value}</div>
                    </OverlayTrigger>
                );
            }
        },

        {
            colId: 'oChains24',
            field: 'oChains24',
            headerName: 'O Chains 24 Mos',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    O Chains<br />24 Mos
                </SortableHeaderComponent>,
            width: 95,
        },

        {
            colId: 'fvCume',
            field: 'fvCume',
            headerName: 'U FV',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    U FV
                </SortableHeaderComponent>,
            width: 80,
        }
    ];

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <>
            <h3>NCS Detail Report</h3>

            <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap' fontSize='14px' style={{ marginBottom: 20 }}>
                <AgGridReact
                    enableCellTextSelection={true}
                    enableRangeSelection={true}
                    columnDefs={columnDefs}
                    defaultColDef={{
                        cellClass: 'center-list-cell',
                        headerClass: 'center-list-header',
                        sortable: true,
                        resizable: true
                    }}
                    rowData={rowData}
                    headerHeight={58}
                    noRowsOverlayComponentFramework={() =>
                        <div>No data for the selected date range...</div>}
                    onGridReady={onGridReady}
                    onGridSizeChanged={onGridSizeChanged}
                    onFirstDataRendered={(e: FirstDataRenderedEvent) => e.api.sizeColumnsToFit()}
                    suppressCellSelection={true}
                    onSortChanged={(e: SortChangedEvent) => {
                        e.api.refreshCells();
                    }}
                    pinnedBottomRowData={props.totals}
                    getRowStyle={(params: any) => {
                        var retColor = {};

                        if (params.data.ctrId == 0) {
                            retColor = { background: 'lightblue', fontWeight: 'bold' };
                        }

                        return retColor;
                    }}
                />
            </GridWrapper>
        </>
    )
}

export default DetailGrid;